import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  FileText,
  Download,
  RefreshCw,
  Building,
  Calendar,
  PieChart,
  BarChart3,
  Loader2,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const FinancialReports = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("summary");
  
  // Filters
  const [period, setPeriod] = useState("ytd");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedOwner, setSelectedOwner] = useState("");
  const [selectedProperty, setSelectedProperty] = useState("");
  
  // Data
  const [owners, setOwners] = useState([]);
  const [properties, setProperties] = useState([]);
  const [summary, setSummary] = useState(null);
  const [plReport, setPlReport] = useState(null);
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});

  const headers = { Authorization: `Bearer ${token}` };

  const fetchInitialData = async () => {
    try {
      const [ownersRes, propertiesRes] = await Promise.all([
        axios.get(`${API}/owners`, { headers }),
        axios.get(`${API}/properties`, { headers })
      ]);
      setOwners(ownersRes.data);
      setProperties(propertiesRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    }
  };

  const fetchSummary = async () => {
    try {
      const res = await axios.get(`${API}/reports/summary`, {
        headers,
        params: { period }
      });
      setSummary(res.data);
    } catch (error) {
      console.error("Summary error:", error);
    }
  };

  const fetchPLReport = async () => {
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (selectedOwner) params.owner_id = selectedOwner;
      if (selectedProperty) params.property_id = selectedProperty;

      const res = await axios.get(`${API}/reports/profit-loss`, { headers, params });
      setPlReport(res.data);
    } catch (error) {
      console.error("P&L error:", error);
    }
  };

  const fetchBalanceSheet = async () => {
    try {
      const params = {};
      if (selectedOwner) params.owner_id = selectedOwner;
      if (selectedProperty) params.property_id = selectedProperty;

      const res = await axios.get(`${API}/reports/balance-sheet`, { headers, params });
      setBalanceSheet(res.data);
    } catch (error) {
      console.error("Balance sheet error:", error);
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchInitialData();
      await fetchSummary();
      setLoading(false);
    };
    init();
  }, [token]);

  useEffect(() => {
    if (activeTab === "summary") {
      fetchSummary();
    } else if (activeTab === "pl") {
      fetchPLReport();
    } else if (activeTab === "balance") {
      fetchBalanceSheet();
    }
  }, [activeTab, period, startDate, endDate, selectedOwner, selectedProperty]);

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const downloadReport = (reportType) => {
    let data, filename;
    
    if (reportType === "pl" && plReport) {
      data = plReport;
      filename = `profit_loss_${plReport.period.start_date}_${plReport.period.end_date}.json`;
    } else if (reportType === "balance" && balanceSheet) {
      data = balanceSheet;
      filename = `balance_sheet_${balanceSheet.as_of_date}.json`;
    } else {
      return;
    }

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Report downloaded!");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-green-700" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="financial-reports-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Financial Reports</h1>
          <p className="text-slate-500 mt-1">P&L statements, balance sheets, and portfolio analytics</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="summary" className="data-[state=active]:bg-white">
            <PieChart className="w-4 h-4 mr-2" />
            Portfolio Summary
          </TabsTrigger>
          <TabsTrigger value="pl" className="data-[state=active]:bg-white">
            <BarChart3 className="w-4 h-4 mr-2" />
            Profit & Loss
          </TabsTrigger>
          <TabsTrigger value="balance" className="data-[state=active]:bg-white">
            <FileText className="w-4 h-4 mr-2" />
            Balance Sheet
          </TabsTrigger>
        </TabsList>

        {/* Portfolio Summary Tab */}
        <TabsContent value="summary">
          <div className="space-y-6">
            {/* Period Selector */}
            <div className="flex gap-2">
              {["ytd", "monthly", "quarterly"].map((p) => (
                <Button
                  key={p}
                  variant={period === p ? "default" : "outline"}
                  size="sm"
                  onClick={() => setPeriod(p)}
                  className={period === p ? "bg-green-700" : ""}
                  data-testid={`period-${p}`}
                >
                  {p === "ytd" ? "Year to Date" : p === "monthly" ? "This Month" : "This Quarter"}
                </Button>
              ))}
            </div>

            {/* Summary Cards */}
            {summary && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-green-100 text-sm">Total Income</p>
                          <p className="text-2xl font-bold mt-1">
                            {formatCurrency(summary.portfolio_summary.total_income)}
                          </p>
                        </div>
                        <TrendingUp className="w-10 h-10 text-green-200" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-red-100 text-sm">Total Expenses</p>
                          <p className="text-2xl font-bold mt-1">
                            {formatCurrency(summary.portfolio_summary.total_expenses)}
                          </p>
                        </div>
                        <TrendingDown className="w-10 h-10 text-red-200" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-blue-100 text-sm">Net Income</p>
                          <p className="text-2xl font-bold mt-1">
                            {formatCurrency(summary.portfolio_summary.net_income)}
                          </p>
                        </div>
                        <DollarSign className="w-10 h-10 text-blue-200" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-purple-100 text-sm">Profit Margin</p>
                          <p className="text-2xl font-bold mt-1">
                            {summary.portfolio_summary.profit_margin}%
                          </p>
                        </div>
                        <PieChart className="w-10 h-10 text-purple-200" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* By Owner Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle>Performance by Owner/LLC</CardTitle>
                    <CardDescription>
                      {summary.period.type === "ytd" ? "Year to Date" : 
                       summary.period.type === "monthly" ? "This Month" : "This Quarter"}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {summary.by_owner.map((owner) => (
                        <div 
                          key={owner.owner_id} 
                          className="p-4 border rounded-lg hover:bg-slate-50 transition-colors"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                                <Building className="w-5 h-5 text-slate-600" />
                              </div>
                              <div>
                                <p className="font-medium">{owner.owner_name}</p>
                                <span className="text-sm text-slate-500">
                                  {owner.properties_count} {owner.properties_count === 1 ? "property" : "properties"}
                                  {owner.owner_type && (
                                    <Badge className="ml-2" variant="outline">
                                      {owner.owner_type.toUpperCase()}
                                    </Badge>
                                  )}
                                </span>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className={`text-lg font-bold ${owner.net_income >= 0 ? "text-green-600" : "text-red-600"}`}>
                                {formatCurrency(owner.net_income)}
                              </p>
                              <p className="text-xs text-slate-500">Net Income</p>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4 pt-3 border-t">
                            <div>
                              <p className="text-xs text-slate-500">Income</p>
                              <p className="font-medium text-green-600">{formatCurrency(owner.total_income)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Expenses</p>
                              <p className="font-medium text-red-600">{formatCurrency(owner.total_expenses)}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </TabsContent>

        {/* Profit & Loss Tab */}
        <TabsContent value="pl">
          <div className="space-y-6">
            {/* Filters */}
            <Card>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      data-testid="start-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      data-testid="end-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Owner/LLC</Label>
                    <Select value={selectedOwner || "all"} onValueChange={(v) => { setSelectedOwner(v === "all" ? "" : v); setSelectedProperty(""); }}>
                      <SelectTrigger data-testid="owner-select">
                        <SelectValue placeholder="All owners" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All owners</SelectItem>
                        {owners.map((o) => (
                          <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Property</Label>
                    <Select value={selectedProperty || "all"} onValueChange={(v) => { setSelectedProperty(v === "all" ? "" : v); setSelectedOwner(""); }}>
                      <SelectTrigger data-testid="property-select">
                        <SelectValue placeholder="All properties" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All properties</SelectItem>
                        {properties.map((p) => (
                          <SelectItem key={p.id} value={p.id}>{p.address}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* P&L Report */}
            {plReport && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Income Section */}
                <Card>
                  <CardHeader className="bg-green-50 border-b">
                    <CardTitle className="text-green-800 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5" />
                      Income
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6 space-y-3">
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-slate-600">Rent Income</span>
                      <span className="font-medium">{formatCurrency(plReport.income.rent_income)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-slate-600">Late Fees</span>
                      <span className="font-medium">{formatCurrency(plReport.income.late_fee_income)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-slate-600">Other Income</span>
                      <span className="font-medium">{formatCurrency(plReport.income.other_income)}</span>
                    </div>
                    <div className="flex justify-between py-3 bg-green-50 px-3 rounded-lg">
                      <span className="font-semibold text-green-800">Total Income</span>
                      <span className="font-bold text-green-800">{formatCurrency(plReport.income.total_income)}</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Expenses Section */}
                <Card>
                  <CardHeader className="bg-red-50 border-b">
                    <CardTitle className="text-red-800 flex items-center gap-2">
                      <TrendingDown className="w-5 h-5" />
                      Expenses
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6 space-y-3">
                    {Object.entries(plReport.expenses.by_category).map(([category, data]) => (
                      <div key={category} className="border-b pb-2">
                        <button
                          onClick={() => toggleCategory(category)}
                          className="w-full flex justify-between items-center py-2 hover:bg-slate-50 rounded"
                        >
                          <span className="text-slate-600 capitalize flex items-center gap-2">
                            {expandedCategories[category] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            {category.replace(/_/g, " ")}
                          </span>
                          <span className="font-medium">{formatCurrency(data.total)}</span>
                        </button>
                        {expandedCategories[category] && data.items.length > 0 && (
                          <div className="ml-6 mt-2 space-y-1">
                            {data.items.map((item, idx) => (
                              <div key={idx} className="flex justify-between text-sm text-slate-500 py-1">
                                <span>{item.description}</span>
                                <span>{formatCurrency(item.amount)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                    <div className="flex justify-between py-3 bg-red-50 px-3 rounded-lg">
                      <span className="font-semibold text-red-800">Total Expenses</span>
                      <span className="font-bold text-red-800">{formatCurrency(plReport.expenses.total_expenses)}</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Net Income */}
                <Card className="lg:col-span-2">
                  <CardContent className="pt-6">
                    <div className={`p-6 rounded-xl ${plReport.net_income >= 0 ? "bg-green-50" : "bg-red-50"}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`text-sm ${plReport.net_income >= 0 ? "text-green-600" : "text-red-600"}`}>
                            Net Income
                          </p>
                          <p className={`text-3xl font-bold ${plReport.net_income >= 0 ? "text-green-800" : "text-red-800"}`}>
                            {formatCurrency(plReport.net_income)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-slate-500">Profit Margin</p>
                          <p className={`text-2xl font-bold ${plReport.net_income >= 0 ? "text-green-700" : "text-red-700"}`}>
                            {plReport.profit_margin}%
                          </p>
                        </div>
                        <Button onClick={() => downloadReport("pl")} variant="outline" data-testid="download-pl">
                          <Download className="w-4 h-4 mr-2" />
                          Download Report
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Balance Sheet Tab */}
        <TabsContent value="balance">
          <div className="space-y-6">
            {/* Filters */}
            <Card>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Owner/LLC</Label>
                    <Select value={selectedOwner || "all"} onValueChange={(v) => { setSelectedOwner(v === "all" ? "" : v); setSelectedProperty(""); }}>
                      <SelectTrigger>
                        <SelectValue placeholder="All owners" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All owners</SelectItem>
                        {owners.map((o) => (
                          <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Property</Label>
                    <Select value={selectedProperty || "all"} onValueChange={(v) => { setSelectedProperty(v === "all" ? "" : v); setSelectedOwner(""); }}>
                      <SelectTrigger>
                        <SelectValue placeholder="All properties" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All properties</SelectItem>
                        {properties.map((p) => (
                          <SelectItem key={p.id} value={p.id}>{p.address}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button onClick={() => downloadReport("balance")} variant="outline" className="w-full" data-testid="download-balance">
                      <Download className="w-4 h-4 mr-2" />
                      Download Report
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Balance Sheet Report */}
            {balanceSheet && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Assets */}
                <Card>
                  <CardHeader className="bg-blue-50 border-b">
                    <CardTitle className="text-blue-800">Assets</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6 space-y-3">
                    <p className="text-sm font-semibold text-slate-500 uppercase">Current Assets</p>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-slate-600">Security Deposits Held</span>
                      <span className="font-medium">{formatCurrency(balanceSheet.assets.current_assets.security_deposits_held)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-slate-600">Accounts Receivable</span>
                      <span className="font-medium">{formatCurrency(balanceSheet.assets.current_assets.accounts_receivable)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-slate-600">Prepaid Rent</span>
                      <span className="font-medium">{formatCurrency(balanceSheet.assets.current_assets.prepaid_rent)}</span>
                    </div>
                    <div className="flex justify-between py-3 bg-blue-50 px-3 rounded-lg">
                      <span className="font-semibold text-blue-800">Total Assets</span>
                      <span className="font-bold text-blue-800">{formatCurrency(balanceSheet.assets.total_assets)}</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Liabilities */}
                <Card>
                  <CardHeader className="bg-amber-50 border-b">
                    <CardTitle className="text-amber-800">Liabilities</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6 space-y-3">
                    <p className="text-sm font-semibold text-slate-500 uppercase">Current Liabilities</p>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-slate-600">Security Deposits Owed</span>
                      <span className="font-medium">{formatCurrency(balanceSheet.liabilities.current_liabilities.security_deposits_owed)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-slate-600">Unpaid Invoices</span>
                      <span className="font-medium">{balanceSheet.liabilities.current_liabilities.unpaid_invoices_count} invoices</span>
                    </div>
                    <div className="flex justify-between py-3 bg-amber-50 px-3 rounded-lg">
                      <span className="font-semibold text-amber-800">Total Liabilities</span>
                      <span className="font-bold text-amber-800">{formatCurrency(balanceSheet.liabilities.total_liabilities)}</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Equity Summary */}
                <Card className="lg:col-span-2">
                  <CardContent className="pt-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="p-4 bg-blue-50 rounded-lg text-center">
                        <p className="text-sm text-blue-600">Total Assets</p>
                        <p className="text-2xl font-bold text-blue-800">{formatCurrency(balanceSheet.assets.total_assets)}</p>
                      </div>
                      <div className="p-4 bg-amber-50 rounded-lg text-center">
                        <p className="text-sm text-amber-600">Total Liabilities</p>
                        <p className="text-2xl font-bold text-amber-800">{formatCurrency(balanceSheet.liabilities.total_liabilities)}</p>
                      </div>
                      <div className={`p-4 rounded-lg text-center ${balanceSheet.equity >= 0 ? "bg-green-50" : "bg-red-50"}`}>
                        <p className={`text-sm ${balanceSheet.equity >= 0 ? "text-green-600" : "text-red-600"}`}>Owner's Equity</p>
                        <p className={`text-2xl font-bold ${balanceSheet.equity >= 0 ? "text-green-800" : "text-red-800"}`}>
                          {formatCurrency(balanceSheet.equity)}
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t flex items-center justify-between text-sm text-slate-500">
                      <span>As of: {balanceSheet.as_of_date}</span>
                      <span>{balanceSheet.properties_count} properties • {balanceSheet.active_leases_count} active leases</span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FinancialReports;
